import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isExternalMedia, isFile, isMedialinkStudentAssignment } from '../../_api/MedialinksApi';
import { getFileTypeDescription } from '../../../../utils/fileType';
import IdentityContext from '../../../../contexts/IdentityContext';
import { formatDate } from '../../../../utils/formatDate';
import formatTeacherName from '../../../../utils/formatTeacherName';
import { Medialink } from '../../_api/MedialinksApi';

const TRANSLATION_KEY = 'MODULES.MATERIAL.COMPONENTS.MEDIALINK-MODALS.OPTIONS';

type MedialinkInfoProps = {
  material: Medialink;
  nodeDescription: string;
  medialinkWithFileDescription?: boolean;
};

function SharedInfo({ material }: Pick<MedialinkInfoProps, 'material'>) {
  const { t } = useTranslation();

  const { identity } = useContext(IdentityContext);

  const allShares = [...material.shares, ...material.assignments];

  if (identity.isTeacherAlike) {
    return (
      <>
        <dt>{t(`${TRANSLATION_KEY}.SHARED.LABEL`)}</dt>
        <dd className="p-mb-2">
          {allShares.length > 0 ? t(`${TRANSLATION_KEY}.SHARED.YES`) : t(`${TRANSLATION_KEY}.SHARED.NO`)}
        </dd>
      </>
    );
  }

  if (allShares.length > 0) {
    let teacherNames = material.shares.map(x => x.user && formatTeacherName(x.user));

    if (isMedialinkStudentAssignment(material)) {
      teacherNames = [...teacherNames, ...material.assignments.map(x => formatTeacherName(x.user))];
    }

    return (
      <>
        <dt>{t(`${TRANSLATION_KEY}.SHARED_BY`)}</dt>
        <dd className="p-mb-2">{teacherNames.join(', ')}</dd>
      </>
    );
  }

  return null;
}

function getContentTypeDescription(contentType: Medialink['contentType']) {
  return contentType.category ? `${contentType.category} - ${contentType.name}` : contentType.name;
}

export function MedialinkInfo({ material, nodeDescription, medialinkWithFileDescription }: MedialinkInfoProps) {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;

  const { t } = useTranslation();

  const { identity } = useContext(IdentityContext);

  const fileType = useMemo(() => {
    if (isExternalMedia(material))
      return t(`FILE_TYPE_DESCRIPTIONS.${getFileTypeDescription(material.externalMedia.type).key}`);

    if (isFile(material))
      return t(`FILE_TYPE_DESCRIPTIONS.${getFileTypeDescription(material.file.s3file.mimeType).key}`);

    return undefined;
  }, [material, t]);

  return (
    <>
      <dt>{t(`${TRANSLATION_KEY}.UPDATED_AT_LABEL`)}</dt>
      <dd>{formatDate(material.updatedAt)}</dd>

      <dt>{t(`${TRANSLATION_KEY}.BELONGS_TO`)}</dt>
      <dd className="p-mb-2">{nodeDescription}</dd>

      {identity.isTeacherAlike && (
        <>
          <dt>{t(`${TRANSLATION_KEY}.GROUP_LABEL`)}</dt>
          <dd className="p-mb-2">{t(material.group === 'teacher' ? 'ROLES.TEACHER' : 'ROLES.STUDENT')}</dd>
        </>
      )}

      {['SO', 'ES', 'VO'].includes(REACT_APP_P_STUDIO_GROUP) && <SharedInfo material={material} />}

      <dt>{t(`${TRANSLATION_KEY}.CONTENTTYPE.LABEL`)}</dt>
      <dd className={fileType || medialinkWithFileDescription ? undefined : 'p-mb-2'}>
        {getContentTypeDescription(material.contentType)}
      </dd>

      {fileType && (
        <>
          <dt>{t(`${TRANSLATION_KEY}.FILETYPE.LABEL`)}</dt>
          <dd>{fileType}</dd>
        </>
      )}
    </>
  );
}

export default function MedialinkInfoWrappedInDefinitionList({
  material,
  nodeDescription,
  medialinkWithFileDescription,
}: MedialinkInfoProps) {
  return (
    <dl className="definition-list">
      <MedialinkInfo
        material={material}
        nodeDescription={nodeDescription}
        medialinkWithFileDescription={medialinkWithFileDescription}
      />
    </dl>
  );
}
